import { Subject } from 'rxjs';
import { Observable, of } from 'rxjs';
import {
  CommunicationNotificationItem,
  CommunicationNotificationItemContext
} from '../models/communication-notification-item';
import { NotificationService } from './notification-service';
import { pdsHttpUtils } from '../pds-http-utils';
import { AjaxResponse } from '../interfaces/AjaxResponse';
import { GetStreamFeedService } from '../services/get-stream-feed-service';
import { ChatMessageStreamActivityUpdate } from 'common-utils/interfaces';
import { StreamActivityChatMessageRI, StreamActivityNotificationRI, ChatMessageActivityNotificationRI, UserSectionBasicRI } from 'common-utils/rest-interfaces';
import { CommunicationNotificationServiceConfig } from '../interfaces/communication-notification-service-config';
import { CommunicationNotificationServiceContext } from '../interfaces/communication-notification-service-context';
import { mergeMap, merge } from 'rxjs/operators';
import { map } from 'rxjs/operators';
import { mapTo } from 'rxjs/operators';
import { publishLast } from 'rxjs/operators';
import { filter } from 'rxjs/operators';
import { refCount } from 'rxjs/operators';
import { pdsLogger } from '../pds-logger';

let log = pdsLogger.requiresLogger('notification-service');

// this class is the base interface of services used by the aggregated-notification-service for the notification indicator
export class CommunicationNotificationService extends NotificationService {
  notificationStream: Observable<CommunicationNotificationItem> = new Subject<CommunicationNotificationItem>();

  type: string = CommunicationNotificationItem.NOTIFICATION_TYPE;
  config: CommunicationNotificationServiceConfig;
  context: CommunicationNotificationServiceContext;

  private getStreamFeed: GetStreamFeedService;
  private feedInitialized: Observable<any>;
  private userNameMap: { [userUid: string]: UserSectionBasicRI } = {};

  constructor(setup: any, getStreamClient: any) {
    super(setup);

    this.config = setup;
    this.context = setup.context;

    let payload = {
      feedUid: this.context.currentUserUid,
      feedGroup: this.config.feedGroup
    };

    if (setup.getStreamFeed != null) {
      this.getStreamFeed = setup.getStreamFeed;
      this.feedInitialized = of(null).pipe(publishLast(), refCount());
    } else {
      let path = (this.config.communicationPath == null) ? '/communication/v1/token' : this.config.communicationPath;
      let url = this.config.domain != null ? this.config.domain + path : path;
      this.feedInitialized = pdsHttpUtils.post(url, payload, {}, pdsHttpUtils.addUCAuthHeader({})).pipe(
        map((response: AjaxResponse) => {
          let data: { feedGroup: string, token: string, uid: string } = JSON.parse(response.data);
          this.getStreamFeed = new GetStreamFeedService(getStreamClient, this.config.feedGroup, `${this.config.feedPrefix}${this.context.currentUserUid}`, data.token, true);
        }),
        publishLast(),
        refCount()
      );
    }
  }

  markAsRead(items: Array<CommunicationNotificationItem>): Observable<any> {
    return this.getStreamFeed.markNotificationsRead(items.map((item) => {
      return item.getstreamGroupId;
    }));
  }

  markAsSeen(items: Array<CommunicationNotificationItem>): Observable<any> {
    return of({});
  }

  initializeNotificationStream(): Observable<any> {
    return this.feedInitialized
      .pipe(map(() => {
        this.notificationStream = this.getStreamFeed.realTimeUpdates()
          .pipe(map((update: ChatMessageStreamActivityUpdate) => {
            if (update.newActivity != null && update.newActivity.operation.indexOf('CREATE') !== -1
              && update.newActivity.message.senderUid !== this.context.currentUserUid) {
              return [update.newActivity];
            }
            return undefined;
          })
          )
          .pipe(filter((activities: Array<StreamActivityChatMessageRI> | undefined) => !!activities))
          .pipe(mergeMap((activities: Array<StreamActivityChatMessageRI>) => {
            // For real-time updates, we do not want the ajax status to get reflected to the
            // main loader (loading progress indicator) because this will block the user from
            // doing his/her work like entering scores.  This even becomes very obvious if there
            // are multiple real-time notifications happening; hence, we are passing 'true' as the
            // second parameter to disable the ajax status from getting emitted.
            return (<any>this.updateUserMapForNotifications(activities, true)).pipe(mapTo(activities));
          }))
          .pipe(map((activities: Array<StreamActivityChatMessageRI>) => {
            let activity = activities[0];
            if (activity != null && activity.group != null) {
              let senderInfo: UserSectionBasicRI = (this.context.userType === 'TEACHER') ? this.userNameMap[activity.topicUid] : this.userNameMap[activity.threadOwnerUid];
              let context: CommunicationNotificationItemContext = {
                activity: activity,
                senderInfo: senderInfo,
                userType: this.context.userType,
                currentUserUid: this.context.currentUserUid,
                getStreamGroupId: activity.group
              };
              return new CommunicationNotificationItem(false, false, context);
            }
            return undefined;
          })
          )
          .pipe(filter((item: CommunicationNotificationItem) => !!item));
        return true;
      })
      );
  }

  getRecentNotifications(limit: number): Observable<Array<CommunicationNotificationItem>> {
    let filterCallBack = (notification: ChatMessageActivityNotificationRI) => {
      let activity = notification.activities[0];
      return activity.operation.indexOf('CREATE') !== -1
        && activity.message.senderUid !== this.context.currentUserUid
        && !notification.is_read
        && notification.group != null;
    };
    return this.feedInitialized
      .pipe(mergeMap(() => this.getStreamFeed.getNotifications(limit)))
      .pipe(mergeMap((notifications: Array<ChatMessageActivityNotificationRI>) => {
        let activities = notifications.map((notification) => {
          return notification.activities[0];
        });
        return this.updateUserMapForNotifications(activities).pipe(mapTo(notifications));
      }))
      .pipe(map((notifications: Array<ChatMessageActivityNotificationRI>) => {
        return notifications.filter(filterCallBack).map((notificationRI: ChatMessageActivityNotificationRI) => {
          let activity: StreamActivityChatMessageRI = notificationRI.activities[0];
          let senderInfo: UserSectionBasicRI = (this.context.userType === 'TEACHER') ? this.userNameMap[activity.topicUid] : this.userNameMap[activity.threadOwnerUid];
          let context: CommunicationNotificationItemContext = {
            activity: activity,
            senderInfo: senderInfo,
            userType: this.context.userType,
            currentUserUid: this.context.currentUserUid,
            getStreamGroupId: notificationRI.group
          };
          return new CommunicationNotificationItem(notificationRI.is_seen, notificationRI.is_read, context);
        });
      })
      );
  }

  private updateUserMapForNotifications(activities: Array<StreamActivityChatMessageRI>, disableAjaxStatus?: boolean): Observable<null> {
    log(`Updating user map for notifications: ${JSON.stringify(activities)}`);

    let userInfoQuery: Array<{ userUid: string, userType: string, sectionUids: Array<string> }> = this.getNewUserInfoFromNotifications(activities);

    log(`Filtered user info request query: ${JSON.stringify(userInfoQuery)}`);
    if (userInfoQuery.length === 0) {
      return of(null);
    }

    let path: string = (this.config.navigationPath == null) ? '/navigation/v1/user_names' : this.config.navigationPath;
    let url: string = this.config.domain != null ? this.config.domain + path : path;

    return pdsHttpUtils.post(url, userInfoQuery, {}, pdsHttpUtils.addUCAuthHeader({}), disableAjaxStatus).pipe(
      map((response: AjaxResponse) => {
        let data: Array<UserSectionBasicRI> = JSON.parse(response.data);
        for (let datum of data) {
          this.userNameMap[datum.userUid] = datum;
        }
        return null;
      })
    );
  }

  private getNewUserInfoFromNotifications(activities: Array<StreamActivityChatMessageRI>): Array<{ userUid: string, userType: string, sectionUids: Array<string> }> {
    let userQueries: Array<{ userUid: string, userType: string, sectionUids: Array<string> }> = [];
    let seenUsers: Set<string> = new Set();

    for (let activity of activities) {
      switch (this.context.userType) {
        case 'TEACHER':
          // for teacher user, topicUid should be studentUid
          // note: when new thread types are added we will have to compute the user type
          if (!seenUsers.has(activity.topicUid)) {
            userQueries.push({
              userUid: activity.topicUid,
              userType: 'STUDENT',
              sectionUids: activity.message.associatedSectionUids
            });
            seenUsers.add(activity.topicUid);
          }
          break;
        default:
          // threadOwner should always be teacher for student-teacher thread
          // note: when new thread types are added we will have to compute the user type
          if (!seenUsers.has(activity.threadOwnerUid)) {
            userQueries.push({
              userUid: activity.threadOwnerUid,
              userType: 'TEACHER',
              sectionUids: activity.message.associatedSectionUids
            });
            seenUsers.add(activity.threadOwnerUid);
          }
      }
    }

    return userQueries;
  }
}
